import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { environment } from '../../environments/environment';

export interface InstagramPost {
  id: string;
  url: string;
  caption: string;
  timestamp: string;
  displayUrl: string;
  location?: {
    name: string;
    id: string;
  };
  hashtags: string[];
  likesCount: number;
  commentsCount: number;
  ownerUsername: string;
  ownerProfilePicUrl: string;
  type: 'external';
}

export interface ExternalEvent {
  id: string;
  title: string;
  description: string;
  startDate: Date;
  location?: string;
  image: string;
  url: string;
  type: 'external';
  source: 'instagram';
  hashtags: string[];
  creator: {
    username: string;
    profilePic: string;
  };
  likesCount: number;
  commentsCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class InstagramScraperService {
  private externalEventsSubject = new BehaviorSubject<ExternalEvent[]>([]);
  public externalEvents$ = this.externalEventsSubject.asObservable();

  private isLoading = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoading.asObservable();

  private lastFetchTime: number = 0;
  private readonly CACHE_DURATION = 30 * 60 * 1000;

  private readonly callable = httpsCallable(
    getFunctions(
      getApps().length ? getApp() : initializeApp(environment.firebaseConfig),
      'us-central1'
    ),
    'fetchInstagramEvents'
  );

  async fetchInstagramEvents(): Promise<ExternalEvent[]> {
    const now = Date.now();

    if (
      this.externalEventsSubject.value.length > 0 &&
      now - this.lastFetchTime < this.CACHE_DURATION
    ) {
      return this.externalEventsSubject.value;
    }

    this.isLoading.next(true);

    try {
      const result = await this.callable({});
      const data = result.data as { success?: boolean; events?: any[] };

      if (data?.success && Array.isArray(data.events)) {
        const externalEvents: ExternalEvent[] = data.events.map(
          (event: any) => ({
            ...event,
            startDate: new Date(event.startDate),
          })
        );

        this.externalEventsSubject.next(externalEvents);
        this.lastFetchTime = now;

        return externalEvents;
      }

      return [];
    } catch (error) {
      console.error('Error fetching Instagram events:', error);
      return [];
    } finally {
      this.isLoading.next(false);
    }
  }

  getCachedExternalEvents(): ExternalEvent[] {
    return this.externalEventsSubject.value;
  }

  clearCache(): void {
    this.externalEventsSubject.next([]);
    this.lastFetchTime = 0;
  }
}

